import { del, get, post } from "@libs:axios";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { CamelCasedPropertiesDeep } from "type-fest/source/camel-cased-properties-deep";

import {
  BotTasksGetSchema,
  CreateBotTaskApiBotTasksPostData,
  DeleteBotTaskApiBotTasksBotTaskIdDeleteData,
  GetBotTaskApiBotTasksBotTaskIdGetData,
} from "@:openapi";

export interface Word {
  start: number;
  end: number;
  word: string;
  score: number;
  // speaker is available in diarization only
  speaker?: string;
}

export interface Segment {
  start: number;
  end: number;
  text: string;
  language: string;
  // words are available in alignment and diarization (not transcription),
  // and only if bot settings have return_words = True
  words?: Word[];
  // speaker is available in diarization only
  speaker?: string;
}

export interface AudioTaskResponse {
  transcription: {
    segments: Segment[];
  };
  diarization: {
    segments: Segment[];
  };
}

export interface BotTasks {
  CREATE: {
    RequestBody: CamelCasedPropertiesDeep<
      CreateBotTaskApiBotTasksPostData["body"]
    >;
    Response: CamelCasedPropertiesDeep<BotTasksGetSchema>;
  };
  GET: {
    RequestParams: CamelCasedPropertiesDeep<
      GetBotTaskApiBotTasksBotTaskIdGetData["path"]
    >;
    Response: CamelCasedPropertiesDeep<BotTasksGetSchema> & AudioTaskResponse;
  };
  DELETE: {
    RequestParams: CamelCasedPropertiesDeep<
      DeleteBotTaskApiBotTasksBotTaskIdDeleteData["path"]
    >;
    Response: void;
  };
}

export const botTasksQueryKeys = createQueryKeys("botTasks", {
  getOne: (params: BotTasks["GET"]["RequestParams"]) => [params],
});

export const useMutationCreateBotTask = (
  options?: UseMutationOptions<
    BotTasks["CREATE"]["Response"],
    AxiosError,
    BotTasks["CREATE"]["RequestBody"]
  >,
) => {
  return useMutation({
    mutationFn: (data: BotTasks["CREATE"]["RequestBody"]) => {
      return post<BotTasks["CREATE"]["Response"]>("/api/bot_tasks", data);
    },
    ...options,
  });
};

export const useQueryBotTask = (
  botTaskId: string,
  options?: Omit<
    UseQueryOptions<BotTasks["GET"]["Response"], AxiosError>,
    "queryKey"
  >,
) => {
  return useQuery({
    queryKey: botTasksQueryKeys.getOne({ botTaskId }).queryKey,
    queryFn: () => {
      return get<BotTasks["GET"]["Response"]>(`/api/bot_tasks/${botTaskId}`);
    },
    ...options,
  });
};

export const useMutationDeleteBotTask = (
  options?: UseMutationOptions<
    BotTasks["DELETE"]["Response"],
    AxiosError,
    string
  >,
) => {
  return useMutation({
    mutationFn: (botTaskId: string) => {
      return del<BotTasks["DELETE"]["Response"]>(`/api/bot_tasks/${botTaskId}`);
    },
    ...options,
  });
};
